import { createContext } from "preact";
import { useContext, useState, useRef, useEffect, useCallback } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { MINIMIZED_STATE, useWidget } from "./WidgetProvider";
import { useAgent } from "./AgentProvider";

const FALLBACK_MAX_VIDEO_TIME = 360; // 6 minutes
const COUNTDOWN_THRESHOLD = 30; // 30 seconds

interface ErrorContextType {
  errorMsg: string;
  setErrorMsg: (msg: string) => void;
  nonFatalErrorMsg: string;
  setNonFatalErrorMsg: (msg: string) => void;
  timeMsg: string;
  setTimeMsg: (msg: string) => void;
  startMaxVideoTimer: () => void;
  resetErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ComponentChildren;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [nonFatalErrorMsg, setNonFatalErrorMsg] = useState<string>("");
  const [timeMsg, setTimeMsg] = useState<string>("");
  const timerRef = useRef<number | null>(null);
  const { agentData } = useAgent();
  const { resetDailyState, setWidgetState } = useWidget();

  // Initial value doesn't matter since it's set in startMaxVideoTimer
  const secondsLeftRef = useRef<number>(FALLBACK_MAX_VIDEO_TIME);

  const resetErrors = useCallback(() => {
    setErrorMsg("");
    setNonFatalErrorMsg("");
    setTimeMsg("");
  }, []);

  const startMaxVideoTimer = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
    }

    // Compute maxVideoTime from current agentData at call time (not closure)
    const currentMaxVideoTime = agentData?.max_conv_dur != null && agentData.max_conv_dur > 0
      ? agentData.max_conv_dur
      : FALLBACK_MAX_VIDEO_TIME;
    // console.log("currentMaxVideoTime", currentMaxVideoTime);
    secondsLeftRef.current = currentMaxVideoTime;
    setTimeMsg("");

    // Start the timer
    timerRef.current = window.setInterval(() => {
      secondsLeftRef.current -= 1;

      // When we reach 30 seconds or less, start showing the countdown
      if (secondsLeftRef.current <= COUNTDOWN_THRESHOLD && secondsLeftRef.current > 0) {
        setTimeMsg(`• ${secondsLeftRef.current}s left`);
      } else if (secondsLeftRef.current === 0) {
        // Timer finished
        if (timerRef.current !== null) {
          setTimeMsg("");
          setErrorMsg("");
          setNonFatalErrorMsg("");
          setWidgetState(MINIMIZED_STATE);
          resetDailyState();
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }, 1000);
  }, [agentData, setWidgetState, resetDailyState]);

  // Restart timer when agentData loads if timer is already running
  useEffect(() => {
    if (timerRef.current !== null && agentData?.max_conv_dur != null && agentData.max_conv_dur > 0) {
      // Timer is running and agentData just loaded, restart with correct value
      startMaxVideoTimer();
    }
  }, [agentData?.max_conv_dur, startMaxVideoTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        errorMsg,
        setErrorMsg,
        nonFatalErrorMsg,
        setNonFatalErrorMsg,
        timeMsg,
        setTimeMsg,
        startMaxVideoTimer,
        resetErrors,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
}

export function useError(): ErrorContextType {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
}
